/* eslint-disable react/jsx-no-undef */
/* eslint-disable camelcase */
import React, { useState, useEffect } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material"
import { api, showWarning } from "../../../../api"

const ModalLotesEgreso = ({ open, onClose, onSave, articulo, bodcodigo }) => {
  const [lotesDisponibles, setLotesDisponibles] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const cantidadAAfectar = parseFloat(articulo?.cant_afectar) || 0

  // ==========================================================================
  // OBTENCIÓN DE LOTES DISPONIBLES Y RESTAURACIÓN DE ESTADO
  // ==========================================================================
  useEffect(() => {
    const fetchLotes = async () => {
      if (!open || !articulo) return

      setIsLoading(true)
      try {
        const payload = {
          invcodigo: articulo.invcodigo,
          bodcodigo,
          artcodigo: articulo.artcodigo,
        }
        const response = await api.post("/IngEgrporSolicitud/getLotesDisponibles", payload)

        let data = []
        if (response?.data?.data) data = response.data.data
        else if (response?.data) data = response.data

        // Mapeamos los datos del backend y restauramos las cantidades si ya se habían seleccionado
        const mappedLotes = data.map((lote) => {
          const lotePrevio = articulo.lotes?.find((l) => l.lotenumero === lote.lotenumero)
          return {
            ...lote,
            cantidad_disponible: parseFloat(lote.cantidad) || 0,
            cantidad_a_extraer: lotePrevio ? parseFloat(lotePrevio.cantidad) : "",
          }
        })

        setLotesDisponibles(mappedLotes)
      } catch (error) {
        console.error("Error al cargar los lotes disponibles:", error)
        showWarning("Ocurrió un error al intentar cargar los lotes disponibles.")
      } finally {
        setIsLoading(false)
      }
    }

    fetchLotes()
  }, [open, articulo, bodcodigo])

  // ==========================================================================
  // MANEJO DE CANTIDADES A EXTRAER
  // ==========================================================================
  const handleCantidadChange = (index, value) => {
    let numValue = parseFloat(value)

    if (isNaN(numValue) || numValue < 0) {
      numValue = "" // Permitimos borrar el campo
    }

    const lote = lotesDisponibles[index]

    // Validación: No puede sacar más del saldo que tiene ese lote específico
    if (numValue > lote.cantidad_disponible) {
      showWarning(`Solo hay ${lote.cantidad_disponible} unidades disponibles en el lote ${lote.lotenumero}.`)
      numValue = lote.cantidad_disponible
    }

    setLotesDisponibles((prev) => {
      const newLotes = [...prev]
      newLotes[index].cantidad_a_extraer = numValue
      return newLotes
    })
  }

  // ==========================================================================
  // CÁLCULOS Y VALIDACIÓN ESTRICTA
  // ==========================================================================
  const totalExtraido = lotesDisponibles.reduce((acc, lote) => acc + (parseFloat(lote.cantidad_a_extraer) || 0), 0)
  const isCuadrado = totalExtraido === cantidadAAfectar

  const handleSave = () => {
    // Cuadre exacto de cantidades
    if (!isCuadrado) {
      return showWarning(
        `La suma de las cantidades extraídas (${totalExtraido}) no cuadra con la cantidad total a despachar (${cantidadAAfectar}).`,
      )
    }

    // Filtramos solo los lotes de los cuales el usuario decidió extraer mercadería
    const lotesSeleccionados = lotesDisponibles
      .filter((lote) => parseFloat(lote.cantidad_a_extraer) > 0)
      .map((lote) => ({
        lotenumero: lote.lotenumero,
        cantidad: parseFloat(lote.cantidad_a_extraer),
        elaboracion: lote.elaboracion,
        vencimiento: lote.vencimiento,
      }))

    // Enviamos el arreglo final al componente padre (DetalleArticulosInvGrid)
    onSave(lotesSeleccionados)
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ backgroundColor: "#196C87", color: "white", py: 1.5 }}>
        Distribución de Egresos por Lote
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Artículo: <b>{articulo?.artcodigo}</b> - {articulo?.artdescri}
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="body2" color="textSecondary">
            Bodega: {bodcodigo}
          </Typography>
          <Typography variant="body2" color={isCuadrado ? "primary" : "error"} fontWeight="bold">
            Total Distribuido: {totalExtraido} / {cantidadAAfectar}
          </Typography>
        </Box>

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell width="25%">Número de Lote</TableCell>
                  <TableCell width="20%">F. Vencimiento</TableCell>
                  <TableCell width="20%">F. Elaboración</TableCell>
                  <TableCell width="15%" align="center">
                    Disponible
                  </TableCell>
                  <TableCell width="20%" align="center">
                    Cant. a Extraer
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lotesDisponibles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3, color: "text.secondary" }}>
                      No se encontraron lotes con saldo disponible en esta bodega.
                    </TableCell>
                  </TableRow>
                ) : (
                  lotesDisponibles.map((row, index) => {
                    const rowSelected = parseFloat(row.cantidad_a_extraer) > 0
                    return (
                      <TableRow key={index} sx={{ backgroundColor: rowSelected ? "#e3f2fd" : "inherit" }}>
                        <TableCell fontWeight="bold">{row.lotenumero}</TableCell>
                        <TableCell>{row.vencimiento}</TableCell>
                        <TableCell>{row.elaboracion}</TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="bold" color="secondary">
                            {row.cantidad_disponible}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <TextField
                            variant="outlined"
                            size="small"
                            fullWidth
                            type="number"
                            value={row.cantidad_a_extraer}
                            onChange={(e) => handleCantidadChange(index, e.target.value)}
                            inputProps={{
                              min: 0,
                              max: row.cantidad_disponible,
                              step: "0.01",
                              style: { textAlign: "center" },
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, backgroundColor: "#f5f7fa" }}>
        <Button onClick={onClose} color="inherit" variant="outlined">
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={!isCuadrado || isLoading}
          sx={{ backgroundColor: "#196C87" }}
        >
          Confirmar Distribución
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ModalLotesEgreso
